import React, { useRef, useState } from "react";
import { Tooltip } from "flowbite-react";
import {
  panelFieldClassName,
  panelFieldInputClassName,
  panelFieldLabelClassName,
} from "../propertyPanelUtils";

type NumberFieldProps = {
  id?: string;
  label: React.ReactNode;
  tooltip?: string;
  value: number | string;
  placeholder?: string;
  readOnly?: boolean;
  disabled?: boolean;
  min?: number;
  max?: number;
  step?: number;
  type?: React.HTMLInputTypeAttribute;
  inputMode?: React.HTMLAttributes<HTMLInputElement>["inputMode"];
  inputClassName?: string;
  labelClassName?: string;
  fieldClassName?: string;
  emptyValue?: number | string;
  onChange?: (
    value: string,
    event: React.SyntheticEvent<HTMLInputElement>,
  ) => void;
  onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  onBeforeInput?: (event: React.FormEvent<HTMLInputElement>) => void;
};

const NumberField: React.FC<NumberFieldProps> = ({
  id,
  label,
  tooltip,
  value,
  placeholder,
  readOnly,
  disabled,
  min,
  max,
  step,
  type = "number",
  inputMode,
  inputClassName = panelFieldInputClassName,
  labelClassName = panelFieldLabelClassName,
  fieldClassName = panelFieldClassName,
  emptyValue = 0,
  onChange,
  onFocus,
  onBlur,
  onKeyDown,
  onBeforeInput,
}) => {
  const [draftValue, setDraftValue] = useState<string | null>(null);
  const isEditingRef = useRef(false);
  const isDirtyRef = useRef(false);
  const visibleLabel = tooltip ?? label;
  /** Inner span lets long locale strings wrap while the outer box keeps inputs aligned. */
  const labelNode = (
    <span className={labelClassName}>
      <span className="min-w-0 max-w-full">{visibleLabel}</span>
    </span>
  );
  const isEditable = !readOnly && !disabled;
  const readOnlyClassName = readOnly
    ? "read-only:cursor-default read-only:border-gray-200 read-only:bg-gray-100 read-only:text-gray-500 read-only:focus:border-gray-200 read-only:focus:ring-0 dark:read-only:border-gray-600 dark:read-only:bg-gray-800 dark:read-only:text-gray-400 dark:read-only:focus:border-gray-600"
    : "";

  const handleFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    if (isEditable) {
      isEditingRef.current = true;
      isDirtyRef.current = false;
      setDraftValue(event.currentTarget.value);
    }

    if (onFocus) {
      onFocus(event);
    } else if (isEditable) {
      event.currentTarget.select();
    }
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const nextValue = event.currentTarget.value;

    if (isEditingRef.current) {
      isDirtyRef.current = true;
      setDraftValue(nextValue);
    }

    if (nextValue !== "") {
      onChange?.(nextValue, event);
    }
  };

  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    if (
      isEditable &&
      isDirtyRef.current &&
      event.currentTarget.value.trim() === ""
    ) {
      onChange?.(String(emptyValue), event);
    }

    isEditingRef.current = false;
    isDirtyRef.current = false;
    setDraftValue(null);
    onBlur?.(event);
  };

  return (
    <label className={fieldClassName}>
      {tooltip && tooltip !== visibleLabel ? (
        <Tooltip content={tooltip}>{labelNode}</Tooltip>
      ) : (
        labelNode
      )}
      <input
        id={id}
        className={`${inputClassName} ${readOnlyClassName}`}
        type={type}
        inputMode={inputMode}
        min={min}
        max={max}
        step={step}
        value={draftValue ?? value}
        placeholder={placeholder}
        readOnly={readOnly}
        disabled={disabled}
        onChange={onChange ? handleChange : undefined}
        onFocus={isEditable || onFocus ? handleFocus : undefined}
        onBlur={isEditable || onBlur ? handleBlur : undefined}
        onKeyDown={onKeyDown}
        onBeforeInput={onBeforeInput}
      />
    </label>
  );
};

export default NumberField;
